export function createPWAInstallState() {
  return {
    started: false,
    installed: false,
    available: false,
    promptEvent: null
  };
}

export function canPromptPWAInstall(state) {
  return Boolean(state?.available && state?.promptEvent && !state?.installed);
}

export function pwaInstallStatusText(state) {
  if (state?.installed) {
    return "App is installed on this device.";
  }
  if (canPromptPWAInstall(state)) {
    return "Install is available from this browser.";
  }
  return "If install is supported here, use your browser's install or Add to Home Screen menu.";
}

export function startPWAInstall(state, options = {}) {
  if (!state || state.started) {
    return () => {};
  }
  state.started = true;

  const win = options.window || globalThis.window;
  const nav = options.navigator || globalThis.navigator;
  state.installed = detectInstalled({ window: win, navigator: nav });

  if (!win?.addEventListener) {
    return () => {};
  }

  const handleBeforeInstallPrompt = (event) => {
    event.preventDefault?.();
    state.promptEvent = event;
    state.available = true;
    options.onChange?.();
  };

  const handleAppInstalled = () => {
    state.installed = true;
    state.available = false;
    state.promptEvent = null;
    options.onInstall?.();
    options.onChange?.();
  };

  win.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
  win.addEventListener("appinstalled", handleAppInstalled);

  return () => {
    win.removeEventListener?.("beforeinstallprompt", handleBeforeInstallPrompt);
    win.removeEventListener?.("appinstalled", handleAppInstalled);
  };
}

export async function promptPWAInstall(state, options = {}) {
  if (!canPromptPWAInstall(state)) {
    return { outcome: "unavailable" };
  }

  const promptEvent = state.promptEvent;
  state.promptEvent = null;
  state.available = false;
  options.onChange?.();

  if (typeof promptEvent.prompt === "function") {
    await promptEvent.prompt();
  }

  const choice = await resolveUserChoice(promptEvent);
  if (choice.outcome === "accepted") {
    state.installed = state.installed || detectInstalled({
      window: options.window || globalThis.window,
      navigator: options.navigator || globalThis.navigator
    });
  }
  options.onChange?.();
  return choice;
}

async function resolveUserChoice(promptEvent) {
  try {
    if (promptEvent?.userChoice && typeof promptEvent.userChoice.then === "function") {
      const result = await promptEvent.userChoice;
      const outcome = String(result?.outcome || "").trim();
      if (outcome === "accepted" || outcome === "dismissed") {
        return { outcome };
      }
    }
  } catch (_error) {
    return { outcome: "dismissed" };
  }
  return { outcome: "accepted" };
}

function detectInstalled(options = {}) {
  const nav = options.navigator;
  if (nav?.standalone === true) {
    return true;
  }
  const matcher = resolveDisplayModeMatcher(options.window);
  return Boolean(matcher?.matches);
}

function resolveDisplayModeMatcher(win) {
  if (!win?.matchMedia) {
    return null;
  }
  try {
    return win.matchMedia("(display-mode: standalone)");
  } catch (_error) {
    return null;
  }
}
